import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Card,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
    ListGroup,
    Badge,
} from 'react-bootstrap';

import { useOrganizationApi } from '@/shared/services/organizationApi';

// Valid relationship types
const RELATIONSHIP_TYPES = [
    { value: 'PARTNERS_WITH', label: 'Partners With' },
    { value: 'COLLABORATES_WITH', label: 'Collaborates With' },
];

/**
 * ManageRelationships component for organization owners/admins to manage relationships
 */
export default function ManageRelationships({
    organizationId,
    organizationName,
    canEdit,
}) {
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Add relationship modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [relationshipType, setRelationshipType] = useState('PARTNERS_WITH');
    const [submitting, setSubmitting] = useState(false);

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [relationshipToDelete, setRelationshipToDelete] = useState(null);

    const orgApi = useOrganizationApi();

    // Load relationships
    const loadRelationships = useCallback(async () => {
        try {
            setLoading(true);
            const data = await orgApi.getRelationships(organizationId);
            setRelationships(data.relationships || []);
        } catch (err) {
            if (err.response?.status === 503) {
                setError('Relationship service is not available');
            } else {
                setError('Failed to load relationships');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [organizationId, orgApi]);

    useEffect(() => {
        loadRelationships();
    }, [loadRelationships]);

    // Organization search with debounce
    useEffect(() => {
        if (!searchQuery || searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        const searchTimeout = setTimeout(async () => {
            try {
                setSearching(true);
                const data = await orgApi.searchOrganizations(searchQuery);
                // Filter out current org and existing relationships
                const existingIds = relationships.map((r) => r.organizationId);
                existingIds.push(organizationId);
                const filtered = (data.organizations || []).filter(
                    (o) => !existingIds.includes(o._id)
                );
                setSearchResults(filtered);
            } catch (err) {
                console.error('Organization search error:', err);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => clearTimeout(searchTimeout);
    }, [searchQuery, orgApi, organizationId, relationships]);

    const handleAddRelationship = async () => {
        if (!selectedOrg) return;

        try {
            setSubmitting(true);
            await orgApi.createRelationship(
                organizationId,
                selectedOrg._id,
                relationshipType
            );
            setSuccess('Relationship created successfully');
            setShowAddModal(false);
            setSelectedOrg(null);
            setSearchQuery('');
            setSearchResults([]);
            loadRelationships();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to create relationship'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDeleteRelationship = (rel) => {
        setRelationshipToDelete(rel);
        setShowDeleteModal(true);
    };

    const handleDeleteRelationship = async () => {
        if (!relationshipToDelete) return;

        try {
            setSubmitting(true);
            await orgApi.deleteRelationship(
                organizationId,
                relationshipToDelete.organizationId,
                relationshipToDelete.type
            );
            setSuccess('Relationship removed successfully');
            setShowDeleteModal(false);
            setRelationshipToDelete(null);
            loadRelationships();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(
                err.response?.data?.message || 'Failed to remove relationship'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const getRelationshipLabel = (type) => {
        const found = RELATIONSHIP_TYPES.find((t) => t.value === type);
        return found ? found.label : type;
    };

    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="mt-2 mb-0 small text-muted">
                        Loading relationships...
                    </p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <strong>Network Connections</strong>
                {canEdit && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowAddModal(true)}
                    >
                        + Add Connection
                    </Button>
                )}
            </Card.Header>
            <Card.Body className="p-0">
                {error && (
                    <Alert
                        variant="danger"
                        className="m-3"
                        dismissible
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" className="m-3">
                        {success}
                    </Alert>
                )}

                {relationships.length === 0 ? (
                    <div className="p-4 text-center text-muted">
                        <p className="mb-2">No connections yet.</p>
                        {canEdit && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowAddModal(true)}
                            >
                                Add First Connection
                            </Button>
                        )}
                    </div>
                ) : (
                    <ListGroup variant="flush">
                        {relationships.map((rel, idx) => (
                            <ListGroup.Item
                                key={`${rel.organizationId}-${rel.type}-${idx}`}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <div>
                                    <Link
                                        to={`/organizations/${rel.organizationId}`}
                                        className="text-decoration-none"
                                    >
                                        <strong>{rel.organizationName}</strong>
                                    </Link>
                                    <br />
                                    <Badge bg="secondary" className="mt-1">
                                        {rel.direction === 'outgoing'
                                            ? '→'
                                            : '←'}{' '}
                                        {getRelationshipLabel(rel.type)}
                                    </Badge>
                                </div>
                                {canEdit && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                            confirmDeleteRelationship(rel)
                                        }
                                    >
                                        Remove
                                    </Button>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Card.Body>

            {/* Add Relationship Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Connection</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Create a connection between{' '}
                        <strong>{organizationName}</strong> and another
                        organization.
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Search Organizations</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search by name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            Type at least 2 characters to search
                        </Form.Text>
                    </Form.Group>

                    {searching && (
                        <div className="text-center py-3">
                            <Spinner
                                animation="border"
                                size="sm"
                                variant="primary"
                            />
                        </div>
                    )}

                    {searchResults.length > 0 && (
                        <ListGroup className="mb-3">
                            {searchResults.slice(0, 5).map((org) => (
                                <ListGroup.Item
                                    key={org._id}
                                    action
                                    active={selectedOrg?._id === org._id}
                                    onClick={() => setSelectedOrg(org)}
                                >
                                    <strong>{org.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                        {org.primarySector}
                                    </small>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}

                    {searchQuery.length >= 2 &&
                        !searching &&
                        searchResults.length === 0 && (
                            <Alert variant="info">
                                No organizations found matching &quot;
                                {searchQuery}
                                &quot;
                            </Alert>
                        )}

                    {selectedOrg && (
                        <Card className="mb-3 bg-light">
                            <Card.Body>
                                <strong>Selected:</strong>
                                <div className="mt-2">
                                    <strong>{selectedOrg.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                        {selectedOrg.primarySector}
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Relationship Type</Form.Label>
                        <Form.Select
                            value={relationshipType}
                            onChange={(e) =>
                                setRelationshipType(e.target.value)
                            }
                        >
                            {RELATIONSHIP_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowAddModal(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddRelationship}
                        disabled={submitting || !selectedOrg}
                    >
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Create Connection'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remove Connection</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {relationshipToDelete && (
                        <p>
                            Are you sure you want to remove the connection with{' '}
                            <strong>
                                {relationshipToDelete.organizationName}
                            </strong>
                            ?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteRelationship}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Remove'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}

ManageRelationships.propTypes = {
    organizationId: PropTypes.string.isRequired,
    organizationName: PropTypes.string.isRequired,
    canEdit: PropTypes.bool,
};

ManageRelationships.defaultProps = {
    canEdit: false,
};
